import {
  validateAchRoutingAndAccount,
  validateCardExpirationStringFields,
  validateCardNumberAndCVV,
  validateFullNameMin2,
} from "@tillix/utils";
import { type } from "arktype";

// --- Card form ---

const QuickPayCardFormFields = type({
  cardHolderName: type("string.trim").narrow((name, ctx) => {
    const err = validateFullNameMin2(name);
    if (err) return ctx.reject({ message: err.message });
    return true;
  }),
  cardNumber: "string.trim",
  expiryMonth: "string.trim",
  expiryYear: "string.trim",
  cvv: "string.trim",
  email: "string.email",
});

export type QuickPayCardFormValues = typeof QuickPayCardFormFields.infer;

export const quickPayCardFormSchema = QuickPayCardFormFields.narrow(
  (d, ctx) => {
    const cardErr = validateCardNumberAndCVV(d.cardNumber, d.cvv);
    if (cardErr !== null) {
      return ctx.reject({
        path: cardErr.path,
        message: cardErr.message,
        actual: cardErr.path[0] === "cardNumber" ? d.cardNumber : d.cvv,
      });
    }

    const expErr = validateCardExpirationStringFields(
      d.expiryMonth,
      d.expiryYear,
      { month: ["expiryMonth"], year: ["expiryYear"] },
    );
    if (expErr !== null) {
      return ctx.reject({
        path: expErr.path,
        message: expErr.message,
        actual: expErr.path[0] === "expiryMonth" ? d.expiryMonth : d.expiryYear,
      });
    }

    return true;
  },
);

// --- Check / ACH form ---

const QuickPayCheckFormFields = type({
  firstName: type("string.trim").narrow((name, ctx) => {
    if (!name) return ctx.reject({ message: "validation.firstNameRequired" });
    return true;
  }),
  lastName: type("string.trim").narrow((name, ctx) => {
    if (!name) return ctx.reject({ message: "validation.lastNameRequired" });
    return true;
  }),
  routingNumber: type("string.digits").and("string.trim"),
  checkAccountNumber: type("string.digits").and("string.trim"),
  checkAccountType: "'Personal' | 'Business'",
  fundingAccountType: "'Checking' | 'Savings'",
  email: "string.email",
  address1: type("string.trim").narrow((addr, ctx) => {
    if (!addr)
      return ctx.reject({
        message: "validation.streetAddressRequired",
      });
    return true;
  }),
});

export type QuickPayCheckFormValues = typeof QuickPayCheckFormFields.infer;

export const quickPayCheckFormSchema = QuickPayCheckFormFields.narrow(
  (d, ctx) => {
    const bankErr = validateAchRoutingAndAccount(
      d.routingNumber,
      d.checkAccountNumber,
    );
    if (bankErr !== null) {
      const path =
        bankErr.path[0] === "accountNumber"
          ? ["checkAccountNumber"]
          : bankErr.path;
      return ctx.reject({
        path,
        message: bankErr.message,
        actual:
          bankErr.path[0] === "routingNumber"
            ? d.routingNumber
            : d.checkAccountNumber,
      });
    }

    return true;
  },
);
